import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useMe, useIsAdmin } from '@/lib/hooks';
import { isMac } from '@/lib/keys';
import { useNotifications } from '@/lib/notifications';
import { openAdmin, openPalette, overlayStore, toggleNotifications } from '@/lib/overlays';
import { goTo, useLink, useRoute, type ViewId } from '@/lib/router';
import { signOut } from '@/lib/session';
import { navDotSource, type NavDotSource } from '@/lib/slots';
import { useStore } from '@/lib/store';
import { Avatar } from '@/ui/Avatar';
import { BrandMark, Icon } from '@/ui/Icon';
import type { IconName } from '@/ui/icons';
import { MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';
import { openAccount } from './AccountDialog';
import { openShortcuts } from './Shortcuts';

// The floating bar at the top: the brand, the main navigation with its sliding highlight, search, the bell
// and your account menu.

interface NavEntry {
  view: ViewId;
  label: string;
  icon: IconName;
  keys: string;
  tip?: string;
}

const NAV: NavEntry[] = [
  { view: 'today', label: 'Home', icon: 'home', keys: 'G T' },
  { view: 'inbox', label: 'Inbox', icon: 'inbox', keys: 'G I' },
  { view: 'people', label: 'People', icon: 'users', keys: 'G P' },
  { view: 'companies', label: 'Companies', icon: 'building', keys: 'G C' },
  { view: 'deals', label: 'Deals', icon: 'deals', keys: 'G D' },
  { view: 'finder', label: 'Finder', icon: 'sparkle', keys: 'G F', tip: 'AI Finder' },
];

export function Topbar() {
  const me = useMe();
  return (
    <header className="topbar" id="topbar">
      <div className="tb-left">
        <HomeBrand workspaceName={me.workspace.name} />
      </div>
      <MainNav />
      <div className="tb-right">
        <button type="button" className="search-pill" id="searchBtn" data-tip="Search and commands" data-key="/" onClick={() => openPalette('')}>
          <Icon name="search" />
          <span>Search or jump to</span>
          <kbd className="kbd">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <Bell />
        <MeButton />
      </div>
    </header>
  );
}

function HomeBrand({ workspaceName }: { workspaceName: string }) {
  const link = useLink({ view: 'today', record: null, admin: null });
  return (
    <a className="brand" aria-label="Clientbook, go to Home" {...link}>
      <BrandMark />
      <span className="brand-txt">
        <b>Clientbook</b>
        <span>{workspaceName}</span>
      </span>
    </a>
  );
}

function MainNav() {
  const route = useRoute();
  const navRef = useRef<HTMLElement>(null);
  const indRef = useRef<HTMLSpanElement>(null);

  // The highlight slides under the active entry; Activity and Bin have no entry, so it fades out there.
  const moveIndicator = useCallback(() => {
    const ind = indRef.current;
    const active = navRef.current?.querySelector<HTMLElement>('.seg-btn.active');
    if (!ind) return;
    if (!active) {
      ind.style.opacity = '0';
      return;
    }
    ind.style.width = `${active.offsetWidth}px`;
    ind.style.transform = `translateX(${active.offsetLeft}px)`;
    ind.style.opacity = '1';
  }, []);

  useLayoutEffect(moveIndicator, [route.view, moveIndicator]);

  useEffect(() => {
    window.addEventListener('resize', moveIndicator);
    window.addEventListener('load', moveIndicator);
    void document.fonts?.ready.then(moveIndicator);
    const t = setTimeout(moveIndicator, 250);
    return () => {
      window.removeEventListener('resize', moveIndicator);
      window.removeEventListener('load', moveIndicator);
      clearTimeout(t);
    };
  }, [moveIndicator]);

  return (
    <nav className="seg" id="seg" aria-label="Main" ref={navRef}>
      <span className="seg-ind" ref={indRef} aria-hidden="true" />
      {NAV.map((entry) => (
        <NavButton key={entry.view} entry={entry} active={route.view === entry.view} />
      ))}
    </nav>
  );
}

function NavButton({ entry, active }: { entry: NavEntry; active: boolean }) {
  const link = useLink({ view: entry.view, record: null, admin: null });
  const dot = navDotSource(entry.view);
  return (
    <a
      className={active ? 'seg-btn active' : 'seg-btn'}
      data-tip={entry.tip ?? entry.label}
      data-key={entry.keys}
      aria-current={active ? 'page' : undefined}
      {...link}
    >
      <Icon name={entry.icon} />
      <span className="lbl">{entry.label}</span>
      {dot ? <NavDot source={dot} /> : null}
    </a>
  );
}

function NavDot({ source }: { source: NavDotSource }) {
  const reason = source();
  return reason ? <span className="seg-dot" role="img" aria-label={reason} /> : null;
}

function Bell() {
  const { unread } = useNotifications();
  const open = useStore(overlayStore, (s) => s.notifications);
  return (
    <button
      type="button"
      className="icon-btn"
      id="bellBtn"
      aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications, all read'}
      data-tip="Notifications"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls="npanel"
      onClick={toggleNotifications}
    >
      <Icon name="bell" />
      <span className={unread ? 'badge' : 'badge zero'} aria-hidden="true">
        {unread > 99 ? '99+' : unread}
      </span>
    </button>
  );
}

function MeButton() {
  const me = useMe();
  const admin = useIsAdmin();
  const route = useRoute();
  const menu = usePopover('menu');
  const user = me.user;
  const elsewhere = !!route.admin || route.view === 'activity' || route.view === 'bin';
  return (
    <>
      <button
        type="button"
        className={elsewhere ? 'me-btn active' : 'me-btn'}
        id="meBtn"
        aria-label={`${user.name}, account menu`}
        {...menu.anchorProps}
      >
        <Avatar name={user.name} hue={user.hue} decorative />
      </button>
      <Popover state={menu} width={270} maxWidth={300} label="Account menu">
        <div className="me-head">
          <Avatar name={user.name} hue={user.hue} size="md" decorative />
          <div className="grow">
            <b>{user.name}</b>
            <span>
              {admin ? 'Super admin' : 'Member'} · {user.email}
            </span>
          </div>
        </div>
        <MenuSep />
        <MenuItem icon="user" onSelect={() => openAccount()}>
          Your account
        </MenuItem>
        <MenuItem icon="activity" keys="G A" onSelect={() => goTo('activity')}>
          Activity this week
        </MenuItem>
        <MenuItem icon="trash" onSelect={() => goTo('bin')}>
          Bin
        </MenuItem>
        {admin ? (
          <MenuItem icon="shield" onSelect={() => openAdmin()}>
            Admin console
          </MenuItem>
        ) : null}
        <MenuSep />
        <MenuItem icon="keyboard" keys="?" onSelect={openShortcuts}>
          Keyboard shortcuts
        </MenuItem>
        <MenuSep />
        <MenuItem icon="logout" onSelect={() => void signOut()}>
          Sign out
        </MenuItem>
      </Popover>
    </>
  );
}
